import React, { useEffect, useCallback, FunctionComponent } from "react";
import { Row, Col, Space, Card, Statistic } from "antd";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { StoreType } from "../../redux/store";
import "./index.scss";
import { NumberOutlined, PercentageOutlined } from "@ant-design/icons";
import fetchPixelTreeStatistics, {
  FetchPixelTreeStatistics,
} from "../../redux/actions/reports/fetchPixelTreeStatistics";
import LoadingGraph from "../../wrappers/LoadingGraph";
import AdsByWeekYoYGraph from "./AdsByWeekYoYGraph";
import AdsByWeekYoYCSV from "./AdsByWeekYoYCSV";
import SMBByWeekYoYGraph from "./SMBByWeekYoYGraph";
import fetchPixelTreeGraphs, {
  FetchPixelTreeGraphs,
} from "../../redux/actions/reports/fetchPixelTreeGraphs";
import {
  AdsByWeekYoYRecord,
  SMBByWeekYoYRecord,
  AdsByMonthTyVsLyRecord,
  SMBByMonthTyVsLyRecord,
} from "../../redux/types/reportTypes";
import AdsByMonthTyVsLyCSV from "./AdsByMonthTyVsLyCSV";
import AdsByMonthTyVsLyGraph from "./AdsByMonthTyVsLyGraph";
import SMBByMonthTyVsLyGraph from "./SMBByMonthTyVsLyGraph";
import SMBByMonthTyVsLyCSV from "./SMBByMonthTyVsLyCSV";

type PixelTreeProps = {
  fetchPixelTreeStatistics: FetchPixelTreeStatistics;
  fetchPixelTreeGraphs: FetchPixelTreeGraphs;

  estimatedAdsTmLoading: boolean;
  mtdAdsPctChgVsLyLoading: boolean;
  mtdAdsPctChgVsLmLoading: boolean;
  estimatedSmbsTmLoading: boolean;
  mtdSmbsPctChgVsLyLoading: boolean;
  mtdSmbsPctChgVsLmLoading: boolean;
  adsByWeekYoYLoading: boolean;
  smbsByWeekYoYLoading: boolean;
  adsByMonthTyVsLyLoading: boolean;
  smbsByMonthTyVsLyLoading: boolean;

  estimatedAdsTm: number | undefined;
  mtdAdsPctChgVsLy: number | undefined;
  mtdAdsPctChgVsLm: number | undefined;
  estimatedSmbsTm: number | undefined;
  mtdSmbsPctChgVsLy: number | undefined;
  mtdSmbsPctChgVsLm: number | undefined;
  adsByWeekYoYRecords: Array<AdsByWeekYoYRecord>;
  smbsByWeekYoYRecords: Array<SMBByWeekYoYRecord>;
  adsByMonthTyVsLyRecords: Array<AdsByMonthTyVsLyRecord>;
  smbsByMonthTyVsLyRecords: Array<SMBByMonthTyVsLyRecord>;
};

export const PixelTree: FunctionComponent<PixelTreeProps> = ({
  fetchPixelTreeStatistics,
  fetchPixelTreeGraphs,
  estimatedAdsTmLoading,
  mtdAdsPctChgVsLyLoading,
  mtdAdsPctChgVsLmLoading,
  estimatedSmbsTmLoading,
  mtdSmbsPctChgVsLyLoading,
  mtdSmbsPctChgVsLmLoading,
  adsByWeekYoYLoading,
  smbsByWeekYoYLoading,
  adsByMonthTyVsLyLoading,
  smbsByMonthTyVsLyLoading,
  estimatedAdsTm,
  mtdAdsPctChgVsLy,
  mtdAdsPctChgVsLm,
  estimatedSmbsTm,
  mtdSmbsPctChgVsLy,
  mtdSmbsPctChgVsLm,
  adsByWeekYoYRecords,
  smbsByWeekYoYRecords,
  adsByMonthTyVsLyRecords,
  smbsByMonthTyVsLyRecords,
}: PixelTreeProps) => {
  const setInitialState = useCallback(() => {
    fetchPixelTreeStatistics();
    fetchPixelTreeGraphs();
  }, [fetchPixelTreeStatistics, fetchPixelTreeGraphs]);

  useEffect(() => {
    setInitialState();
  }, [setInitialState]);

  return (
    <Space direction="vertical" className="pixel-tree">
      <Row>
        <Col span={24}>
          <Row>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Estimated Ads TM"
                  value={estimatedAdsTm}
                  prefix={<NumberOutlined spin={estimatedAdsTmLoading} />}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="MTD Ads % chg vs LY"
                  value={mtdAdsPctChgVsLy}
                  prefix={<PercentageOutlined spin={mtdAdsPctChgVsLyLoading} />}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="MTD Ads % chg vs LM"
                  value={mtdAdsPctChgVsLm}
                  prefix={<PercentageOutlined spin={mtdAdsPctChgVsLmLoading} />}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Estimated SMBs TM"
                  value={estimatedSmbsTm}
                  prefix={<NumberOutlined spin={estimatedSmbsTmLoading} />}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="MTD smb % chg vs LY"
                  value={mtdSmbsPctChgVsLy}
                  prefix={<PercentageOutlined spin={mtdSmbsPctChgVsLyLoading} />}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="MTD smb % chg vs LM"
                  value={mtdSmbsPctChgVsLm}
                  prefix={<PercentageOutlined spin={mtdSmbsPctChgVsLmLoading} />}
                />
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <LoadingGraph
            title="Ads By Month - TY vs LY"
            loading={adsByMonthTyVsLyLoading}
            records={adsByMonthTyVsLyRecords}
            Component={AdsByMonthTyVsLyGraph}
            Extra={AdsByMonthTyVsLyCSV}
          />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <LoadingGraph
            title="SMBs By Month - TY vs LY"
            loading={smbsByMonthTyVsLyLoading}
            records={smbsByMonthTyVsLyRecords}
            Component={SMBByMonthTyVsLyGraph}
            Extra={SMBByMonthTyVsLyCSV}
          />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <LoadingGraph
            title="Ads By Week - Year Over Year"
            loading={adsByWeekYoYLoading}
            records={adsByWeekYoYRecords}
            Component={AdsByWeekYoYGraph}
            Extra={AdsByWeekYoYCSV}
          />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <LoadingGraph
            title="SMBs By Week - Year Over Year"
            loading={smbsByWeekYoYLoading}
            records={smbsByWeekYoYRecords}
            Component={SMBByWeekYoYGraph}
            Extra={AdsByWeekYoYCSV}
            countKey="smb_ct"
          />
        </Col>
      </Row>
    </Space>
  );
};

const mapDispatchToProps = {
  fetchPixelTreeStatistics,
  fetchPixelTreeGraphs,
};

const mapStateToProps = ({
  reports: {
    estimated_ads_tm: { loading: estimatedAdsTmLoading, records: estimatedAdsTmRecords },
    mtd_ads_pct_chg_vs_ly: {
      loading: mtdAdsPctChgVsLyLoading,
      records: mtdAdsPctChgVsLyRecords,
    },
    mtd_ads_pct_chg_vs_lm: {
      loading: mtdAdsPctChgVsLmLoading,
      records: mtdAdsPctChgVsLmRecords,
    },
    estimated_smbs_tm: {
      loading: estimatedSmbsTmLoading,
      records: estimatedSmbsTmRecords,
    },
    mtd_smbs_pct_chg_vs_ly: {
      loading: mtdSmbsPctChgVsLyLoading,
      records: mtdSmbsPctChgVsLyRecords,
    },
    mtd_smbs_pct_chg_vs_lm: {
      loading: mtdSmbsPctChgVsLmLoading,
      records: mtdSmbsPctChgVsLmRecords,
    },
    ads_by_week_yoy: { loading: adsByWeekYoYLoading, records: adsByWeekYoYRecords },
    smbs_by_week_yoy: { loading: smbsByWeekYoYLoading, records: smbsByWeekYoYRecords },
    ads_by_month_ty_vs_ly: {
      loading: adsByMonthTyVsLyLoading,
      records: adsByMonthTyVsLyRecords,
    },
    smbs_by_month_ty_vs_ly: {
      loading: smbsByMonthTyVsLyLoading,
      records: smbsByMonthTyVsLyRecords,
    },
  },
}: StoreType) => ({
  estimatedAdsTmLoading,
  mtdAdsPctChgVsLyLoading,
  mtdAdsPctChgVsLmLoading,
  estimatedSmbsTmLoading,
  mtdSmbsPctChgVsLyLoading,
  mtdSmbsPctChgVsLmLoading,
  adsByWeekYoYLoading,
  smbsByWeekYoYLoading,
  adsByMonthTyVsLyLoading,
  smbsByMonthTyVsLyLoading,
  estimatedAdsTm: estimatedAdsTmRecords[0]?.estimated_count,
  mtdAdsPctChgVsLy: parseFloat(String(mtdAdsPctChgVsLyRecords[0]?.pct_change * 100)),
  mtdAdsPctChgVsLm: parseFloat(String(mtdAdsPctChgVsLmRecords[0]?.pct_change * 100)),
  estimatedSmbsTm: estimatedSmbsTmRecords[0]?.estimated_count,
  mtdSmbsPctChgVsLy: parseFloat(String(mtdSmbsPctChgVsLyRecords[0]?.pct_change * 100)),
  mtdSmbsPctChgVsLm: parseFloat(String(mtdSmbsPctChgVsLmRecords[0]?.pct_change * 100)),
  adsByWeekYoYRecords,
  smbsByWeekYoYRecords,
  adsByMonthTyVsLyRecords,
  smbsByMonthTyVsLyRecords,
});

PixelTree.propTypes = {
  fetchPixelTreeStatistics: PropTypes.func.isRequired,
  estimatedAdsTmLoading: PropTypes.bool.isRequired,
  mtdAdsPctChgVsLyLoading: PropTypes.bool.isRequired,
  mtdAdsPctChgVsLmLoading: PropTypes.bool.isRequired,
  estimatedSmbsTmLoading: PropTypes.bool.isRequired,
  mtdSmbsPctChgVsLyLoading: PropTypes.bool.isRequired,
  mtdSmbsPctChgVsLmLoading: PropTypes.bool.isRequired,
  adsByWeekYoYLoading: PropTypes.bool.isRequired,
  smbsByWeekYoYLoading: PropTypes.bool.isRequired,
  adsByMonthTyVsLyLoading: PropTypes.bool.isRequired,
  smbsByMonthTyVsLyLoading: PropTypes.bool.isRequired,
  estimatedAdsTm: PropTypes.number.isRequired,
  mtdAdsPctChgVsLy: PropTypes.number.isRequired,
  mtdAdsPctChgVsLm: PropTypes.number.isRequired,
  estimatedSmbsTm: PropTypes.number.isRequired,
  mtdSmbsPctChgVsLy: PropTypes.number.isRequired,
  mtdSmbsPctChgVsLm: PropTypes.number.isRequired,
  adsByWeekYoYRecords: PropTypes.array.isRequired,
  smbsByWeekYoYRecords: PropTypes.array.isRequired,
  adsByMonthTyVsLyRecords: PropTypes.array.isRequired,
  smbsByMonthTyVsLyRecords: PropTypes.array.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(PixelTree);
